import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import { adminSubscriptionApi } from '../api/adminSubscription.api';
import { adminPlanTierApi } from '../api/adminSubscriptionPlan.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';

function getInitials(firstName, lastName) {
  const name = `${firstName || ''} ${lastName || ''}`.trim();
  if (!name) return '??';
  return name.split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase();
}

function StatusBadge({ status }) {
  const normalized = (status || '').toLowerCase();
  let bg = '#F1F5F9';
  let color = '#475569';
  let border = '#E2E8F0';

  if (normalized === 'active') {
    bg = '#F0FDF4';
    color = '#15803D';
    border = '#b7e5bb';
  } else if (normalized === 'trialing' || normalized === 'trial') {
    bg = '#EFF6FF';
    color = '#1D4ED8';
    border = '#BFDBFE';
  } else if (normalized === 'past_due' || normalized === 'unpaid') {
    bg = '#FFFBEB';
    color = '#B45309';
    border = '#FDE68A';
  } else if (normalized === 'canceled' || normalized === 'cancelled' || normalized === 'inactive') {
    bg = '#FEF2F2';
    color = '#B91C1C';
    border = '#FECACA';
  } else if (normalized === 'paused') {
    bg = '#F5F3FF';
    color = '#6D28D9';
    border = '#DDD6FE';
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 8px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        background: bg,
        color: color,
        border: `1px solid ${border}`,
        textTransform: 'uppercase',
      }}
    >
      {status}
    </span>
  );
}

export default function SubscriptionList() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [planTiers, setPlanTiers] = useState([]);
  const [error, setError] = useState(null);
  
  // Local filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [billingCycleFilter, setBillingCycleFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [expiringSoonFilter, setExpiringSoonFilter] = useState('');
  const [cardBrandFilter, setCardBrandFilter] = useState('');
  const [exporting, setExporting] = useState(false);

  // Applied filter state
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    status: '',
    billingCycle: '',
    planId: '',
    expiringSoon: '',
    paymentMethodBrand: '',
  });

  const {
    page,
    limit,
    total,
    loading,
    setLoading,
    resetPage,
    applyPaginationMeta,
    paginationProps,
  } = usePagination(10);

  // Load plan tiers for filter dropdown
  useEffect(() => {
    const fetchPlanTiers = async () => {
      const result = await apiHandler(() => adminPlanTierApi.list());
      if (result.success) {
        setPlanTiers(unwrapData(result) || []);
      }
    };
    fetchPlanTiers();
  }, []);

  // Fetch subscriptions from backend
  const fetchSubscriptions = useCallback(async (pageNum = page) => {
    setLoading(true);
    setError(null);
    const params = {
      page: pageNum,
      limit,
      search: appliedFilters.search || undefined,
      status: appliedFilters.status || undefined,
      billingCycle: appliedFilters.billingCycle || undefined,
      planId: appliedFilters.planId || undefined,
      expiringSoon: appliedFilters.expiringSoon || undefined,
      paymentMethodBrand: appliedFilters.paymentMethodBrand || undefined,
    };

    const result = await apiHandler(() => adminSubscriptionApi.list(params));
    if (result.success) {
      const responseData = unwrapData(result);
      setSubscriptions(responseData.subscriptions || []);
      applyPaginationMeta(responseData);
    } else {
      setError(result.message || 'Failed to load user subscriptions');
    }
    setLoading(false);
  }, [appliedFilters, page, limit, applyPaginationMeta, setLoading]);

  const handleExport = async () => {
    setExporting(true);
    const filters = {
      search: appliedFilters.search || undefined,
      status: appliedFilters.status || undefined,
      billingCycle: appliedFilters.billingCycle || undefined,
      planId: appliedFilters.planId || undefined,
      expiringSoon: appliedFilters.expiringSoon || undefined,
      paymentMethodBrand: appliedFilters.paymentMethodBrand || undefined,
    };
    const result = await apiHandler(() => adminSubscriptionApi.export(filters));
    if (result.success) {
      alert('CSV Export job has been queued in the background. You will receive an email with the download link shortly.');
    } else {
      alert(result.message || 'Failed to queue export job');
    }
    setExporting(false);
  };

  useEffect(() => {
    fetchSubscriptions(page);
  }, [page, appliedFilters]);

  const handleApplyFilters = () => {
    setAppliedFilters({
      search: searchTerm,
      status: statusFilter,
      billingCycle: billingCycleFilter,
      planId: planFilter,
      expiringSoon: expiringSoonFilter,
      paymentMethodBrand: cardBrandFilter,
    });
    resetPage();
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setBillingCycleFilter('');
    setPlanFilter('');
    setExpiringSoonFilter('');
    setCardBrandFilter('');
    setAppliedFilters({
      search: '',
      status: '',
      billingCycle: '',
      planId: '',
      expiringSoon: '',
      paymentMethodBrand: '',
    });
    resetPage();
  };

  return (
    <>
      <PageHeader
        inline
        trail={[{ label: 'Payment Management' }, { label: 'User Subscription' }]}
      />

      {error && (
        <div
          className="fade-up"
          style={{
            marginBottom: 16,
            padding: '12px 16px',
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: 10,
            color: '#B91C1C',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* Subscription Table and Filters Container */}
      <div className="fade-up delay-1">
        <div className="section-card">
          <div className="cm-toolbar">
            <div className="cm-toolbar-start">
              {/* Status Filter */}
              <select
                className="cm-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Status"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="trialing">Trialing</option>
                <option value="past_due">Past Due</option>
                <option value="canceled">Canceled</option>
                <option value="unpaid">Unpaid</option>
                <option value="paused">Paused</option>
              </select>

              {/* Card Brand Filter */}
              <select
                className="cm-filter-select"
                value={cardBrandFilter}
                onChange={(e) => setCardBrandFilter(e.target.value)}
                aria-label="Card Brand"
              >
                <option value="">All Brands</option>
                <option value="visa">Visa</option>
                <option value="mastercard">Mastercard</option>
                <option value="amex">Amex</option>
                <option value="discover">Discover</option>
              </select>

              {/* Expiring Soon Filter */}
              <select
                className="cm-filter-select"
                value={expiringSoonFilter}
                onChange={(e) => setExpiringSoonFilter(e.target.value)}
                aria-label="Expiring Soon"
              >
                <option value="">All Expirations</option>
                <option value="true">Expiring in 7 Days</option>
              </select>

              {/* Billing Cycle Filter */}
              <select
                className="cm-filter-select"
                value={billingCycleFilter}
                onChange={(e) => setBillingCycleFilter(e.target.value)}
                aria-label="Billing Cycle"
              >
                <option value="">All Cycles</option>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </select>

              {/* Plan Filter */}
              <select
                className="cm-filter-select"
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                aria-label="Plan Tier"
              >
                <option value="">All Plan Tiers</option>
                {planTiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name}
                  </option>
                ))}
              </select>

              {/* Search wrap */}
              <div className="cm-search-wrap">
                <i className="fas fa-magnifying-glass" aria-hidden />
                <input
                  type="text"
                  className="cm-search-input"
                  placeholder="Search name, email, or mobile…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                />
              </div>
            </div>
            
            <div className="cm-toolbar-end">
              <button
                type="button"
                className="cm-apply-btn"
                onClick={handleApplyFilters}
                disabled={loading}
                style={{ marginRight: 8 }}
              >
                <i className="fas fa-check" /> Apply
              </button>
              <button
                type="button"
                className="sp-btn sp-btn-secondary"
                onClick={handleExport}
                disabled={loading || exporting}
                style={{ marginRight: 8, height: '36px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                <i className="fas fa-file-csv" /> {exporting ? 'Exporting...' : 'Export CSV'}
              </button>
              <button
                type="button"
                className="cm-reset-link"
                onClick={handleResetFilters}
              >
                Reset filters
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 1000 }}>
              <thead>
                <tr>
                  <th>User Details</th>
                  <th>Plan Info</th>
                  <th>Stripe Config</th>
                  <th>Interval</th>
                  <th>Period Dates</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#64748B' }}>
                      Loading subscriptions…
                    </td>
                  </tr>
                ) : subscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: '#64748B' }}>
                      No subscriptions found matching filters.
                    </td>
                  </tr>
                ) : (
                  subscriptions.map((sub) => (
                    <tr key={sub.id} className="user-row">
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: 'linear-gradient(135deg,#102A43,#074f78)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 11,
                              fontWeight: 700,
                              color: '#fff',
                              flexShrink: 0,
                            }}
                          >
                            {getInitials(sub.user?.firstName, sub.user?.lastName)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, color: '#0F172A' }}>
                              {sub.user ? `${sub.user.firstName || ''} ${sub.user.lastName || ''}`.trim() : 'N/A'}
                            </div>
                            <div style={{ fontSize: 11, color: '#64748B' }}>{sub.user?.email || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13, color: '#0F172A' }}>
                          {sub.plan?.planTier?.name || 'Unknown Tier'}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748B' }}>
                          {sub.plan?.price ? `$${sub.plan.price} ${sub.plan.currency || 'USD'}` : '—'}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: '#475569' }}>
                          Sub: <span style={{ color: '#0284C7' }}>{(sub.stripeSubscriptionId || '').slice(0, 15)}...</span>
                        </div>
                        <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: '#64748B' }}>
                          Cust: {(sub.stripeCustomerId || '').slice(0, 15)}...
                        </div>
                      </td>
                      <td style={{ textTransform: 'capitalize', fontSize: 12, fontWeight: 500, color: '#475569' }}>
                        {sub.billingCycle || 'month'}
                      </td>
                      <td style={{ fontSize: 11, color: '#475569' }}>
                        <div>
                          Start: <strong>{sub.startDate ? new Date(sub.startDate).toLocaleDateString() : 'N/A'}</strong>
                        </div>
                        <div>
                          End: <strong>{sub.endDate ? new Date(sub.endDate).toLocaleDateString() : 'Open'}</strong>
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={sub.status} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="sp-btn sp-btn-secondary sp-btn-sm"
                          onClick={() => navigate(`/subscriptions/${sub.id}`)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination {...paginationProps} />
      </div>
    </>
  );
}
